import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { EditorComponent, EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

import { ThemeService } from '@reforgium/presentia';

import { BackToTopComponent, BackToTopViewport } from 'components/back-to-top';

import { AccessibilityService } from 'features/accessibility/accessibility.service';
import { readonlyDocumentStyles } from 'features/accessibility/readonly-document-styles';

import {
  createDocumentHeaderHtml,
  createDocumentSignatureHtml,
  createImagePasteHtml,
  getImageFileFromDataTransfer,
  insertSingletonBlock,
  moveBlockToDocumentEnd,
  normalizeTables,
  restorePastedHeaderEmblem,
} from './redactor.helpers';
import { RedactorService } from './redactor.service';

const DOCUMENT_HEADER_SELECTOR = '[data-document-block="header"]';
const DOCUMENT_SIGNATURE_SELECTOR = '[data-document-block="signature"]';

@Component({
  selector: 'app-redactor',
  template: `
    <div class="redactor-shell">
      @defer (on immediate) {
        <editor
          #editor
          [init]="init()"
          [initialValue]="content()"
          [disabled]="readonly()"
          (onInit)="handleEditorInit($event.editor)"
          (onInput)="handleEditorChange($event.editor)"
          (onChange)="handleEditorChange($event.editor)"
          (onUndo)="handleEditorChange($event.editor)"
          (onRedo)="handleEditorChange($event.editor)"
          licenseKey="gpl"
        />

        <app-back-to-top [viewport]="scrollViewport()" />
      } @placeholder {
        <div class="redactor-placeholder" role="status" aria-label="Loading editor">
          <span></span>
          <span></span>
          <span></span>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .redactor-shell,
    editor {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .redactor-placeholder {
      display: grid;
      gap: 0.75rem;
      width: 100%;
      min-height: 768px;
      padding: 1.5rem;
      box-sizing: border-box;
      border: 1px solid var(--border-color);
      border-radius: var(--radius-lg);
      background: var(--surface-neutral);
    }

    .redactor-placeholder span {
      display: block;
      width: 100%;
      height: 1rem;
      border-radius: 0.25rem;
      background: var(--primary-50);
    }

    .redactor-placeholder span:nth-child(2) {
      width: 82%;
    }

    .redactor-placeholder span:nth-child(3) {
      width: 64%;
    }
  `,
  providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }],
  host: {
    '(document:keydown)': 'print($event)',
  },
  imports: [EditorModule, BackToTopComponent],
})
export class RedactorComponent {
  readonly = input(false, { transform: booleanAttribute });
  fitToWidth = input(false, { transform: booleanAttribute });
  documentTitle = input('');
  content = model('<h1>Название документа</h1><p style="text-align: justify">Текст документа.</p>');
  emblemSrc = input('/assets/images/kyrgyz-republic-emblem.webp');
  documentChange = output<string>();

  themeService = inject(ThemeService);
  redactorService = inject(RedactorService);

  editor = viewChild<EditorComponent>('editor');
  private lastEditorContent: string | undefined;
  private documentResizeObserver?: ResizeObserver;
  private readonly accessibility = inject(AccessibilityService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly initializedEditor = signal<NonNullable<EditorComponent['editor']> | null>(null);

  readonly scrollViewport = signal<BackToTopViewport | null>(null);

  init = computed<EditorComponent['init']>(() => ({
    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,
    base_url: '/tinymce',
    suffix: '.min',
    language: 'ru',
    language_url: '/tinymce/langs/ru.js',
    skin_url: this.themeService.isLight() ? '/tinymce/skins/ui/oxide' : '/tinymce/skins/ui/oxide-dark',
    // The document is rendered on a white paper surface in both app themes.
    // Keep TinyMCE's dark UI skin, but use the light content stylesheet so
    // dark-theme text does not become low-contrast on the white document.
    content_css: '/tinymce/skins/content/default/content.min.css',
    menubar: this.readonly() ? false : 'edit insert format table',
    toolbar: this.readonly()
      ? false
      : 'undo redo | accordion accordionremove | ' +
        'blocks fontsize | bold italic underline strikethrough | ' +
        'align numlist bullist | documentheader1 documentheader2 documentsignature | link image table | ' +
        'lineheight outdent indent | forecolor backcolor removeformat | ' +
        'charmap emoticons | fullscreen preview | anchor',
    toolbar_mode: 'sliding',
    font_size_input_default_unit: 'pt',
    plugins: [
      'advlist',
      'autolink',
      'lists',
      'link',
      'image',
      'charmap',
      'preview',
      'anchor',
      'searchreplace',
      'visualblocks',
      'code',
      'fullscreen',
      'insertdatetime',
      'media',
      'table',
    ],
    setup: (editor) => {
      editor.on(
        'paste',
        (event) => {
          void this.handleClipboardImagePaste(editor, event);
        },
        true,
      );

      editor.on(
        'dragover',
        (event) => {
          if (getImageFileFromDataTransfer(event.dataTransfer)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true,
      );
      editor.on(
        'drop',
        (event) => {
          void this.handleImageDrop(editor, event);
        },
        true,
      );
      editor.ui.registry.addButton('documentheader1', {
        text: 'Шапка Кабинет Министров',
        tooltip: 'Вставить шапку документа',
        onAction: () =>
          insertSingletonBlock(editor, DOCUMENT_HEADER_SELECTOR, createDocumentHeaderHtml(this.emblemSrc()), 'start'),
      });
      editor.ui.registry.addButton('documentheader2', {
        text: 'Шапка Министерство Финансов',
        tooltip: 'Вставить шапку документа',
        onAction: () =>
          insertSingletonBlock(
            editor,
            DOCUMENT_HEADER_SELECTOR,
            createDocumentHeaderHtml(this.emblemSrc(), 2),
            'start',
          ),
      });

      editor.ui.registry.addButton('documentsignature', {
        text: 'Подпись',
        tooltip: 'Вставить место для подписи в конец документа',
        onAction: () => insertSingletonBlock(editor, DOCUMENT_SIGNATURE_SELECTOR, createDocumentSignatureHtml(), 'end'),
      });
    },
    paste_data_images: true,
    automatic_uploads: false,
    paste_postprocess: (_editor, args) => {
      normalizeTables(args.node);
      restorePastedHeaderEmblem(args.node, this.emblemSrc());
    },
    table_sizing_mode: 'relative',
    table_default_styles: {
      'width': '100%',
      'max-width': '100%',
      'border-collapse': 'collapse',
    },
    directionality: 'ltr',
    newline_behavior: 'block',
    forced_root_block_attrs: { dir: 'ltr', style: 'text-align: justify' },
    branding: false,
    promotion: false,
    statusbar: false,
    height: this.readonly() ? 'max(480px, calc(100dvh - 190px))' : 670,
    content_style: `
      html {
        -webkit-text-size-adjust: none;
        text-size-adjust: none;
        background: #fff;
      }
      body,
      body * {
        box-sizing: border-box;
        font-family: Arial, Helvetica, sans-serif;
        color: #111827;
      }
      body {
        width: 31cm;
        min-height: 29.7cm;
        margin: 0 auto;
        padding: 2cm 2cm 2cm 3cm;
        direction: ltr;
        font-size: 14pt;
        line-height: 1.5;
        -webkit-text-size-adjust: none;
        text-size-adjust: none;
        background: #fff;
        overflow-wrap: anywhere;
      }
      body * {
        direction: ltr;
      }
      h1,
      h2,
      h3,
      h4 {
        margin: .6em 0;
        font-weight: 700;
      }
      p { margin: .4em 0; }
      a {
        color: #2563eb;
        text-decoration: underline;
      }
      table {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: auto !important;
        margin-right: auto !important;
        border-collapse: collapse;
      }
      table td,
      table th {
        overflow-wrap: anywhere;
        vertical-align: middle;
      }
      table img {
        max-width: 100%;
        height: auto;
      }
      .document-header-table {
        table-layout: fixed;
      }
      .document-header-table td {
        text-indent: 0;
      }
      .document-signature-table,
      .document-signature-table td {
        border: 0 !important;
      }
      @media print {
        body {
          zoom: 1 !important;
          width: 31cm;
          min-height: 29.7cm;
          margin: 0;
          padding: 2cm 2cm 2cm 3cm;
          direction: ltr;
        }
      }
    `,
    forced_root_block: 'p',
    // valid_elements: 'h1,h2,h3,p,ul,ol,li,a[href|target=_blank],strong/b,em/i,u,span[style],br',
    // valid_styles: { '*': 'text-align,text-transform,font-size' },
    block_formats:
      '\u0422\u0435\u043a\u0441\u0442=editor-text; ' +
      '\u0410\u0431\u0437\u0430\u0446=editor-paragraph; ' +
      '\u0417\u0430\u0433\u043e\u043b\u043e\u0432\u043e\u043a 1=editor-heading-1; ' +
      '\u0417\u0430\u0433\u043e\u043b\u043e\u0432\u043e\u043a 2=editor-heading-2; ' +
      '\u0417\u0430\u0433\u043e\u043b\u043e\u0432\u043e\u043a 3=editor-heading-3; ' +
      '\u0417\u0430\u0433\u043e\u043b\u043e\u0432\u043e\u043a 4=editor-heading-4',
    formats: {
      'editor-text': {
        block: 'p',
        styles: {
          'text-indent': '0',
          'text-align': 'justify',
          'font-weight': '400',
          'margin-top': '.4em',
          'margin-bottom': '.4em',
        },
      },
      'editor-paragraph': {
        block: 'p',
        styles: {
          'text-align': 'justify',
          'text-indent': '12.5mm',
        },
      },
      'editor-heading-1': {
        block: 'h1',
        styles: {
          'text-align': 'center',
          'font-weight': '700',
          'text-indent': '0',
          'margin-top': '1.5rem',
          'margin-bottom': '1.5rem',
        },
      },
      'editor-heading-2': {
        block: 'h2',
        styles: {
          'text-align': 'left',
          'text-indent': '12.5mm',
        },
      },
      'editor-heading-3': {
        block: 'h3',
        styles: {
          'font-weight': '700',
          'text-indent': '0',
          'margin-top': '4.5rem',
        },
        onformat: moveBlockToDocumentEnd,
      },
      'editor-heading-4': {
        block: 'h4',
        styles: {
          'font-weight': '700',
          'text-indent': '0',
          'margin-top': '7.5rem',
        },
        onformat: moveBlockToDocumentEnd,
      },
    },
  }));

  constructor() {
    effect(() => {
      const settings = this.accessibility.settings();
      const editor = this.initializedEditor();

      if (!editor || !this.readonly() || !this.fitToWidth()) return;
      const doc = editor.getDoc();
      const language = this.hostElement.nativeElement.closest('[lang]')?.getAttribute('lang') ?? 'ru';
      const container = editor.getContainer();
      const frame = container.querySelector('iframe');

      doc.documentElement.lang = language;
      editor.getBody().setAttribute('role', 'document');
      editor.getBody().removeAttribute('aria-label');
      container.removeAttribute('role');
      container.removeAttribute('aria-disabled');
      if (frame) frame.title = this.documentTitle() || (language === 'ky' ? 'Документтин тексти' : 'Текст документа');
      let style = doc.getElementById('document-accessibility');

      if (!style) {
        style = doc.createElement('style');
        style.id = 'document-accessibility';
        doc.head.appendChild(style);
      }

      style.textContent = readonlyDocumentStyles(settings);
      this.fitDocumentToWidth(editor);
    });
    effect(() => {
      const editor = this.editor();

      editor && this.redactorService.init(editor);
    });
    effect(() => {
      const editor = this.editor()?.editor;
      const content = this.content();

      if (!editor?.initialized) return;

      const editorContent = editor.getContent({ format: 'raw' });

      if (editorContent === content || this.lastEditorContent === content) return;

      this.lastEditorContent = content;
      editor.setContent(content);
    });

    let cleanupWin: Window | undefined;
    let cleanupFn: ((ev: KeyboardEvent) => void) | undefined;

    const key = setTimeout(() => {
      const redactor = this.editor()?.editor;
      const win = redactor?.getWin?.();

      if (!win || !redactor) return;

      const handler = (ev: KeyboardEvent) => {
        const isCtrl = ev.ctrlKey || ev.metaKey;

        if (isCtrl && ev.code === 'KeyP') {
          ev.preventDefault();
          redactor.execCommand('mcePrint');
        }
      };

      win.addEventListener('keydown', handler, { capture: true });
      cleanupWin = win;
      cleanupFn = handler;
    }, 600);

    inject(DestroyRef).onDestroy(() => {
      this.documentResizeObserver?.disconnect();
      clearTimeout(key);
      cleanupWin?.removeEventListener('keydown', cleanupFn!, { capture: true });
      this.redactorService.dispose();
      this.editor()?.ngOnDestroy();
    });
  }

  handleEditorInit(editor: NonNullable<EditorComponent['editor']>): void {
    this.initializedEditor.set(editor);
    this.fitDocumentToWidth(editor);

    const win = editor.getWin();
    const doc = editor.getDoc();

    this.scrollViewport.set({
      getScrollTop: () => {
        return doc.scrollingElement?.scrollTop ?? win.scrollY ?? 0;
      },

      setScrollTop: (value) => {
        const scrollingElement = doc.scrollingElement;

        if (scrollingElement) {
          scrollingElement.scrollTop = value;

          return;
        }

        win.scrollTo(0, value);
      },

      listen: (listener) => {
        win.addEventListener('scroll', listener, {
          passive: true,
        });

        return () => {
          win.removeEventListener('scroll', listener);
        };
      },
    });
  }

  fitDocumentToWidth(editor: NonNullable<EditorComponent['editor']>): void {
    if (!this.readonly() || !this.fitToWidth()) return;

    const frame = editor.iframeElement;
    const body = editor.getBody();

    if (!frame || !body) return;

    const updateScale = () => {
      const pageWidth = Number.parseFloat(editor.getWin().getComputedStyle(body).width);

      if (!pageWidth || !frame.clientWidth) return;

      body.style.zoom = window.matchMedia('(max-width: 768px)').matches
        ? String(Math.min(1, frame.clientWidth / pageWidth))
        : '1';
    };

    this.documentResizeObserver?.disconnect();
    this.documentResizeObserver = new ResizeObserver(updateScale);
    this.documentResizeObserver.observe(frame);
    updateScale();
  }

  handleEditorChange(editor: EditorComponent['editor']) {
    if (editor.getBody().querySelector('img[src^="blob:"]')) {
      void this.normalizeBlobImages(editor).then(() => this.emitEditorContent(editor));

      return;
    }

    this.emitEditorContent(editor);
  }
  print(event: KeyboardEvent) {
    const isCtrl = event.ctrlKey || event.metaKey;
    const isP = event.code === 'KeyP';

    if (isCtrl && isP) {
      event.preventDefault();
      event.stopPropagation();
      this.redactorService.print();
    }
  }

  private async handleClipboardImagePaste(
    editor: NonNullable<EditorComponent['editor']>,
    event: ClipboardEvent,
  ): Promise<void> {
    const clipboardData = event.clipboardData;
    const imageFile = getImageFileFromDataTransfer(clipboardData);

    if (!imageFile) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const html = clipboardData?.getData('text/html') ?? '';
    const plainText = clipboardData?.getData('text/plain') ?? '';
    const bookmark = editor.selection.getBookmark(2, true);

    try {
      const imageSrc = await this.readFileAsDataUrl(imageFile);
      const content = createImagePasteHtml(editor.getDoc(), html, plainText, imageSrc);

      if (editor.destroyed) {
        return;
      }

      editor.undoManager.transact(() => {
        editor.selection.moveToBookmark(bookmark);
        editor.insertContent(content);
      });
      await this.normalizeBlobImages(editor);

      if (editor.destroyed) {
        return;
      }

      editor.focus();
      editor.nodeChanged();
      this.emitEditorContent(editor);
    } catch {
      editor.notificationManager.open({
        text: 'Не удалось вставить изображение из буфера обмена.',
        type: 'error',
      });
    }
  }

  private async handleImageDrop(editor: NonNullable<EditorComponent['editor']>, event: DragEvent): Promise<void> {
    const dataTransfer = event.dataTransfer;
    const imageFile = getImageFileFromDataTransfer(dataTransfer);

    if (!imageFile) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const html = dataTransfer?.getData('text/html') ?? '';
    const plainText = dataTransfer?.getData('text/plain') ?? '';
    const bookmark = editor.selection.getBookmark(2, true);

    try {
      const imageSrc = await this.readFileAsDataUrl(imageFile);
      const content = createImagePasteHtml(editor.getDoc(), html, plainText, imageSrc);

      if (editor.destroyed) {
        return;
      }

      editor.undoManager.transact(() => {
        editor.selection.moveToBookmark(bookmark);
        editor.insertContent(content);
      });
      await this.normalizeBlobImages(editor);

      if (editor.destroyed) {
        return;
      }

      editor.focus();
      editor.nodeChanged();
      this.emitEditorContent(editor);
    } catch {
      editor.notificationManager.open({
        text: 'Не удалось вставить изображение.',
        type: 'error',
      });
    }
  }

  private async normalizeBlobImages(editor: NonNullable<EditorComponent['editor']>): Promise<void> {
    const images = Array.from(editor.getBody().querySelectorAll<HTMLImageElement>('img[src^="blob:"]'));

    await Promise.all(
      images.map(async (image) => {
        const source = image.getAttribute('src');

        if (!source) {
          return;
        }

        try {
          const response = await fetch(source);

          if (!response.ok) {
            return;
          }

          image.setAttribute('src', await this.readFileAsDataUrl(await response.blob()));
        } catch {
          // Keep the source unchanged so the failed URL remains diagnosable.
        }
      }),
    );
  }

  private readFileAsDataUrl(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Image data is unavailable'));
      };

      reader.onerror = () => reject(reader.error ?? new Error('Image data cannot be read'));
      reader.readAsDataURL(file);
    });
  }

  private emitEditorContent(editor: NonNullable<EditorComponent['editor']>): void {
    const content = editor.getContent({ format: 'raw' });

    this.lastEditorContent = content;
    this.content.set(content);
    this.documentChange.emit(content);
  }
}
